import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import {
  createTenantVoiceNumber,
  deleteTenantVoiceNumber,
  getGlobalVoiceNumbers,
  getTenantVoiceNumbers,
  updateTenantVoiceNumber,
} from '../../../api/backendApi';
import SupportFormModal from '../../support/SupportFormModal';

const formatE164Display = (e164) => {
  const digits = String(e164 || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 ${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
  }
  if (digits.length === 10) {
    return `+1 ${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
  return e164 || '';
};

const numberOptionLabel = (e164, friendly) => {
  const primary = formatE164Display(e164) || e164;
  const name = String(friendly || '').trim();
  if (name && name !== e164 && name !== primary) return `${primary} — ${name}`;
  return primary;
};

const poolFriendly = (row) =>
  row?.friendlyName || row?.label || row?.displayName || row?.name || '';

const isPoolActive = (row) => {
  const s = String(row?.status || 'active').toLowerCase();
  return s === 'active' || s === 'available';
};

function isUnassignedNumbersResponse(err) {
  const msg = String(err?.message || '');
  return msg.includes('API Error (403)') || msg.includes('"Forbidden"');
}

function numbersLoadMessage(err) {
  const message = String(err?.message || '');
  if (message.includes('tenant_conflict') || message.includes('Conflicting access context')) {
    return 'Phone lines could not be loaded for this tenant.';
  }
  return message || 'Failed to load numbers';
}

const DEFAULT_PURPOSES = [
  { id: 'sms', label: 'SMS default' },
  { id: 'voiceOutbound', label: 'Outbound caller ID default' },
];

const capChips = (caps) => {
  const out = [];
  if (caps?.sms) out.push(<Chip key="sms" label="SMS" size="small" color="primary" sx={{ mr: 0.5 }} />);
  if (caps?.voice || caps?.sipCallerId) {
    out.push(<Chip key="voice" label="Voice" size="small" color="secondary" sx={{ mr: 0.5 }} />);
  }
  return out.length ? out : <Chip label="—" size="small" />;
};

const modeChips = (row) => {
  const chips = [];
  if (row?.capabilities?.sms && row?.smsShareMode) {
    chips.push(<Chip key="smsm" label={`SMS ${row.smsShareMode}`} size="small" sx={{ mr: 0.5 }} />);
  }
  if ((row?.capabilities?.voice || row?.capabilities?.sipCallerId) && row?.voiceShareMode) {
    chips.push(<Chip key="voicem" label={`Voice ${row.voiceShareMode}`} size="small" sx={{ mr: 0.5 }} />);
  }
  return chips.length ? chips : <Chip label="—" size="small" />;
};

const defaultChips = (arr) => {
  const set = new Set(Array.isArray(arr) ? arr : []);
  const shown = DEFAULT_PURPOSES.filter((p) => set.has(p.id));
  if (!shown.length) return <Chip label="—" size="small" />;
  return shown.map((p) => <Chip key={p.id} label={p.label} size="small" sx={{ mr: 0.5 }} />);
};

function AddEditDialog({
  open,
  onClose,
  onSubmit,
  initial,
  inventoryOptions,
  inventoryLoading,
}) {
  const isEdit = Boolean(initial?.id);
  const [e164, setE164] = useState('');
  const [sms, setSms] = useState(false);
  const [voice, setVoice] = useState(false);
  const [voiceShareMode, setVoiceShareMode] = useState('shared');
  const [smsShareMode, setSmsShareMode] = useState('shared');
  const [status, setStatus] = useState('active');
  const [defaultFor, setDefaultFor] = useState([]);
  const [err, setErr] = useState('');

  useEffect(() => {
    setE164(initial?.e164 || '');
    setSms(!!initial?.capabilities?.sms);
    setVoice(!!(initial?.capabilities?.voice || initial?.capabilities?.sipCallerId));
    setVoiceShareMode(initial?.voiceShareMode || 'shared');
    setSmsShareMode(initial?.smsShareMode || 'shared');
    setStatus(initial?.status || 'active');
    const existing = Array.isArray(initial?.defaultFor) ? initial.defaultFor : [];
    setDefaultFor(existing.filter((p) => DEFAULT_PURPOSES.some((d) => d.id === p)));
    setErr('');
  }, [initial, open]);

  const toggleDefault = (purpose, checked) => {
    setDefaultFor((prev) => {
      const next = new Set(prev);
      if (checked) next.add(purpose);
      else next.delete(purpose);
      return Array.from(next);
    });
  };

  const handleSubmit = async () => {
    if (!isEdit && !e164) {
      setErr('Select a number from global inventory');
      return;
    }
    if (!sms && !voice) {
      setErr('Enable Voice and/or SMS');
      return;
    }
    const payload = {
      capabilities: {
        sms,
        voice,
        sipCallerId: voice,
      },
      voiceShareMode: voice ? voiceShareMode : null,
      smsShareMode: sms ? smsShareMode : null,
      status,
      defaultFor,
    };
    if (!isEdit) payload.e164 = e164;
    try {
      await onSubmit(payload);
    } catch (e) {
      setErr(String(e?.message || 'Failed to save'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Edit Number Assignment' : 'Add Number'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isEdit ? (
            <Box sx={{ fontSize: 15, fontWeight: 600 }}>
              {numberOptionLabel(e164, initial?.label)}
            </Box>
          ) : (
            <FormControl fullWidth>
              <InputLabel id="inv-label">Number</InputLabel>
              <Select
                labelId="inv-label"
                label="Number"
                value={e164}
                onChange={(e) => setE164(e.target.value)}
                disabled={inventoryLoading}
              >
                <MenuItem value="">
                  {inventoryLoading
                    ? 'Loading inventory…'
                    : inventoryOptions.length === 0
                      ? 'No eligible inventory numbers'
                      : 'Select from global inventory…'}
                </MenuItem>
                {inventoryOptions.map((n) => (
                  <MenuItem key={n.e164} value={n.e164}>
                    {numberOptionLabel(n.e164, poolFriendly(n))}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Stack spacing={0.5}>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={<Checkbox checked={voice} onChange={(e) => setVoice(e.target.checked)} />}
                label="Voice allowed"
              />
              <FormControlLabel
                control={<Checkbox checked={sms} onChange={(e) => setSms(e.target.checked)} />}
                label="SMS allowed"
              />
            </Stack>
            <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
              Voice and SMS are independent. Inbound Voice is out of scope.
            </Box>
          </Stack>

          {voice && (
            <FormControl>
              <Box sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>Voice mode</Box>
              <RadioGroup
                row
                value={voiceShareMode}
                onChange={(e) => setVoiceShareMode(e.target.value)}
              >
                <FormControlLabel value="shared" control={<Radio />} label="Shared" />
                <FormControlLabel value="dedicated" control={<Radio />} label="Dedicated" />
              </RadioGroup>
            </FormControl>
          )}

          {sms && (
            <FormControl>
              <Box sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>SMS mode</Box>
              <RadioGroup
                row
                value={smsShareMode}
                onChange={(e) => setSmsShareMode(e.target.value)}
              >
                <FormControlLabel value="shared" control={<Radio />} label="Shared" />
                <FormControlLabel value="dedicated" control={<Radio />} label="Dedicated" />
              </RadioGroup>
              {e164 === '+13648884343' && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  US number cannot be enabled for SMS until A2P registration is ready.
                </Alert>
              )}
            </FormControl>
          )}

          <Stack spacing={0.5}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              {DEFAULT_PURPOSES.map((p) => {
                const disabled =
                  (p.id === 'sms' && !sms) || (p.id === 'voiceOutbound' && !voice);
                return (
                  <FormControlLabel
                    key={p.id}
                    control={(
                      <Checkbox
                        checked={defaultFor.includes(p.id)}
                        disabled={disabled}
                        onChange={(e) => toggleDefault(p.id, e.target.checked)}
                      />
                    )}
                    label={p.label}
                  />
                );
              })}
            </Stack>
            <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
              Defaults are used when Outbound / SMS settings select Shared or Dedicated.
            </Box>
          </Stack>

          <FormControl fullWidth>
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>

          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>{isEdit ? 'Save' : 'Add'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PhoneNumbersTab({ tenantId, tenantName = '', onNumbersChanged, canManage = false }) {
  const [rows, setRows] = useState([]);
  const [pool, setPool] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [error, setError] = useState('');
  const [dlgOpen, setDlgOpen] = useState(false);
  const [dlgInitial, setDlgInitial] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const tenantLabel = tenantName || 'this tenant';

  const assignedE164 = useMemo(
    () => new Set(rows.map((r) => r.e164).filter(Boolean)),
    [rows],
  );

  const inventoryOptions = useMemo(() => {
    return (pool || [])
      .filter((n) => n?.e164 && isPoolActive(n) && !assignedE164.has(n.e164))
      .sort((a, b) => String(a.e164).localeCompare(String(b.e164)));
  }, [pool, assignedE164]);

  const refresh = async () => {
    if (!tenantId) return;
    setLoading(true);
    setError('');
    try {
      const resp = await getTenantVoiceNumbers(tenantId);
      const list = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
      setRows(list);
    } catch (e) {
      if (isUnassignedNumbersResponse(e)) {
        setRows([]);
        return;
      }
      setError(numbersLoadMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const loadInventory = async () => {
    setInventoryLoading(true);
    try {
      const resp = await getGlobalVoiceNumbers();
      if (resp?.__unsupported) {
        setPool([]);
        return;
      }
      const list = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
      setPool(list);
    } catch (e) {
      setPool([]);
      setError(String(e?.message || 'Failed to load global inventory'));
    } finally {
      setInventoryLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [tenantId]);

  const onAdd = async () => {
    setDlgInitial(null);
    setDlgOpen(true);
    await loadInventory();
  };
  const onEdit = (row) => { setDlgInitial(row); setDlgOpen(true); };

  const onSubmit = async (payload) => {
    setBusy(true);
    try {
      if (dlgInitial?.id) await updateTenantVoiceNumber(tenantId, dlgInitial.id, payload);
      else await createTenantVoiceNumber(tenantId, payload);
      setDlgOpen(false);
      await refresh();
      if (typeof onNumbersChanged === 'function') onNumbersChanged();
    } catch (e) {
      const msg = String(e?.message || 'Failed to save');
      setError(msg);
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (row) => {
    const isDefault = Array.isArray(row?.defaultFor) && row.defaultFor.length > 0;
    const ok = window.confirm(isDefault ? 'This number is set as a default. Remove anyway?' : 'Delete this number?');
    if (!ok) return;
    setBusy(true);
    try {
      await deleteTenantVoiceNumber(tenantId, row.id);
      await refresh();
      if (typeof onNumbersChanged === 'function') onNumbersChanged();
    } catch (e) {
      setError(String(e?.message || 'Failed to delete'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid #e5e7eb' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Box component="h3" sx={{ m: 0, fontSize: 18, fontWeight: 600 }}>Phone Numbers</Box>
          <Box sx={{ mt: 0.5, fontSize: 13, color: 'text.secondary' }}>
            {canManage
              ? 'Assign inventory numbers to this tenant. Choose Voice/SMS and Shared/Dedicated. Outbound and SMS settings select which assigned number is used.'
              : 'Assigned tenant lines. Contact your Signal Bridge admin for PSTN access and settings.'}
          </Box>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<Add />} onClick={onAdd} disabled={busy}>Add Number</Button>
        )}
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : error ? null : rows.length === 0 ? (
        canManage ? (
          <Box sx={{ color: 'text.secondary', fontSize: 14 }}>No numbers assigned.</Box>
        ) : (
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc' }}>
            <Box component="h4" sx={{ m: 0, fontSize: 16, fontWeight: 600 }}>No phone line is assigned</Box>
            <Box sx={{ mt: 1, fontSize: 14, color: 'text.secondary', maxWidth: 560 }}>
              {tenantLabel} does not have a phone line yet. Contact Signal Bridge support and ask for a line to be assigned.
            </Box>
            <Button variant="contained" sx={{ mt: 2 }} onClick={() => setTicketOpen(true)}>
              Request a line
            </Button>
          </Paper>
        )
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Number</TableCell>
              <TableCell>Use</TableCell>
              <TableCell>Modes</TableCell>
              <TableCell>Defaults</TableCell>
              <TableCell>Status</TableCell>
              {canManage && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{numberOptionLabel(r.e164, r.label)}</TableCell>
                <TableCell>{capChips(r.capabilities)}</TableCell>
                <TableCell>{modeChips(r)}</TableCell>
                <TableCell>{defaultChips(r.defaultFor)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={r.status || 'active'}
                    color={(r.status || 'active') === 'active' ? 'success' : 'default'}
                  />
                </TableCell>
                {canManage && (
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="Edit">
                        <span>
                          <IconButton size="small" onClick={() => onEdit(r)}><Edit fontSize="small" /></IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <span>
                          <IconButton size="small" onClick={() => onDelete(r)} disabled={busy}><Delete fontSize="small" /></IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <SupportFormModal
        isOpen={ticketOpen}
        onClose={() => setTicketOpen(false)}
        initialType="account"
        initialTitle={`Assign a phone line to ${tenantLabel}`}
        initialDescription={`Please assign a phone line to ${tenantLabel}${tenantId ? ` (${tenantId})` : ''}. This tenant has no phone numbers yet.`}
      />
      <AddEditDialog
        open={dlgOpen}
        onClose={() => setDlgOpen(false)}
        onSubmit={onSubmit}
        initial={dlgInitial}
        inventoryOptions={inventoryOptions}
        inventoryLoading={inventoryLoading}
      />
    </Paper>
  );
}

export { formatE164Display, numberOptionLabel };
